import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { request } from '../../api/client';
import { useAuthStore } from '../../store/auth';

/*
 * Team chat — client for the /chat API (see the shared chat contract: REST + polling, no websocket).
 * Every id is a STRING on the wire, and every list comes back already sorted by the server:
 * conversations by last activity, messages ASCENDING by id.
 */

export type ChatConversationType = 'COMPANY' | 'TEAM' | 'GROUP' | 'DIRECT';

export interface ChatPerson {
  id: string;
  name: string;
  /** Primary role code, e.g. SALES_PERSON. */
  role: string;
  roleLabel: string;
  photoUrl: string | null;
}

export interface ChatLastMessage {
  id: string;
  senderId: string;
  senderName: string;
  body: string | null;
  hasAttachments: boolean;
  createdAt: string;
}

export interface ChatConversation {
  id: string;
  type: ChatConversationType;
  title: string | null;
  memberCount: number;
  unread: number;
  lastMessage: ChatLastMessage | null;
  /** DIRECT only — the other person. */
  otherUser: ChatPerson | null;
}

export interface ChatMember extends ChatPerson {
  isOwner: boolean;
}

export interface ChatConversationDetail extends ChatConversation {
  members: ChatMember[];
  canManage: boolean;
}

export interface ChatMessage {
  id: string;
  conversationId: string;
  senderId: string;
  senderName: string;
  senderPhotoUrl: string | null;
  body: string | null;
  attachments: string[];
  createdAt: string;
  mine: boolean;
}

/**
 * Contract limit — the server enforces it too; the UI just stops the user earlier.
 * Team chat on the web is TEXT ONLY: there is no way to attach a photo from the composer. Messages
 * that already carry attachments (older ones, or ones sent from another client) still render them.
 */
export const CHAT_BODY_MAX = 4000;

/** Poll cadences from the contract. */
export const THREAD_POLL_MS = 4_000;
export const LIST_POLL_MS = 15_000;

export const chatKeys = {
  all: ['chat'] as const,
  list: ['chat', 'conversations'] as const,
  unread: ['chat', 'unread-count'] as const,
  detail: (id: string) => ['chat', 'conversation', id] as const,
  people: (q: string) => ['chat', 'people', q] as const,
};

export const chatApi = {
  conversations: () => request<ChatConversation[]>({ url: '/chat/conversations' }),
  unreadCount: () => request<{ count: number }>({ url: '/chat/unread-count' }),
  people: (q: string) => request<ChatPerson[]>({ url: '/chat/people', params: q ? { q } : undefined }),
  direct: (userId: string) => request<ChatConversation>({ url: '/chat/direct', method: 'POST', data: { userId } }),
  createGroup: (title: string, memberIds: string[]) =>
    request<ChatConversation>({ url: '/chat/groups', method: 'POST', data: { title, memberIds } }),
  detail: (id: string) => request<ChatConversationDetail>({ url: `/chat/conversations/${id}` }),
  rename: (id: string, title: string) =>
    request<ChatConversationDetail>({ url: `/chat/conversations/${id}`, method: 'PATCH', data: { title } }),
  addMembers: (id: string, userIds: string[]) =>
    request<ChatConversationDetail>({ url: `/chat/conversations/${id}/members`, method: 'POST', data: { userIds } }),
  removeMember: (id: string, userId: string) =>
    request<{ ok: true }>({ url: `/chat/conversations/${id}/members/${userId}`, method: 'DELETE' }),
  messages: (id: string, params: { before?: string; after?: string; limit?: number }) =>
    request<ChatMessage[]>({ url: `/chat/conversations/${id}/messages`, params }),
  send: (id: string, payload: { body?: string; attachments?: string[] }) =>
    request<ChatMessage>({ url: `/chat/conversations/${id}/messages`, method: 'POST', data: payload }),
  markRead: (id: string, messageId: string) =>
    request<{ ok: true }>({ url: `/chat/conversations/${id}/read`, method: 'POST', data: { messageId } }),
};

/**
 * The conversation list — `GET /chat/conversations`, polled every 15 s and on focus. Shared by the
 * full-screen page and the floating chat panel (react-query dedupes them by key).
 *
 * The list carries every one of my conversations, so its unread sum IS `/chat/unread-count` — the
 * badge is kept in step with what is on screen instead of waiting for the badge's own poll.
 */
export function useChatConversations(enabled = true) {
  const qc = useQueryClient();
  const listQ = useQuery({
    queryKey: chatKeys.list,
    queryFn: chatApi.conversations,
    enabled,
    refetchInterval: LIST_POLL_MS,
    refetchOnWindowFocus: true,
    staleTime: 5_000,
  });
  useEffect(() => {
    if (!enabled || !listQ.data) return;
    const count = listQ.data.reduce((s, c) => s + (c.unread || 0), 0);
    qc.setQueryData(chatKeys.unread, { count });
  }, [enabled, listQ.data, qc]);
  return listQ;
}

/** Client-side search over the list: title, last message text, last sender. */
export function filterConversations(rows: ChatConversation[], search: string): ChatConversation[] {
  const q = search.trim().toLowerCase();
  if (!q) return rows;
  return rows.filter((c) =>
    conversationTitle(c).toLowerCase().includes(q)
    || (c.lastMessage?.body ?? '').toLowerCase().includes(q)
    || (c.lastMessage?.senderName ?? '').toLowerCase().includes(q));
}

/** `true` when the list call failed because this session is not staff (farmer / retailer only). */
export const isChatStaffOnlyError = (e: unknown): boolean =>
  (e as any)?.response?.data?.error?.code === 'CHAT_STAFF_ONLY';

/** Error text from the API envelope (the client interceptor synthesises one for network failures). */
export const chatErr = (e: any, fallback: string): string =>
  e?.response?.data?.error?.message ?? e?.message ?? fallback;

/** A conversation's display name. DIRECT rooms are titled by the server with the other person's name. */
export function conversationTitle(c: Pick<ChatConversation, 'type' | 'title' | 'otherUser'>): string {
  if (c.type === 'DIRECT') return c.otherUser?.name ?? c.title ?? 'Direct message';
  if (c.type === 'COMPANY') return c.title || 'Everyone';
  return c.title || (c.type === 'TEAM' ? 'Team' : 'Group');
}

/** "You: …", "Ravi: …" (group rooms) or just the text; a photo-only message reads "📷 Photo". */
export function conversationPreview(c: ChatConversation, meId?: string): string {
  const m = c.lastMessage;
  if (!m) return c.type === 'DIRECT' ? 'Start the conversation' : 'No messages yet';
  const text = m.body?.trim() || (m.hasAttachments ? '📷 Photo' : '');
  if (meId && String(m.senderId) === String(meId)) return `You: ${text}`;
  if (c.type !== 'DIRECT') return `${m.senderName.split(' ')[0]}: ${text}`;
  return text;
}

/** Numeric compare for string ids (they are bigints on the server — never compare them as text). */
export function idGt(a: string, b: string): boolean {
  try { return BigInt(a) > BigInt(b); } catch { return Number(a) > Number(b); }
}

/**
 * Staff test for the chat feature — the contract's rule, verbatim: a user whose effective roles are
 * NOT only FARMER / RETAILER. Stricter than `isCustomer()` (primary role only) in exactly the case
 * that matters: a staff member who additionally holds a customer role still gets chat.
 */
export function isChatStaff(user: { role?: string; roles?: string[] } | null | undefined): boolean {
  if (!user) return false;
  const roles = user.roles?.length ? user.roles : (user.role ? [user.role] : []);
  if (!roles.length) return false;
  return roles.some((r) => r !== 'FARMER' && r !== 'RETAILER');
}

/**
 * The Team chat nav badge — `GET /chat/unread-count`, polled the same way `useBadges` polls
 * `/badges` (interval + refetch on focus). Only runs for staff; a customer session never calls it
 * (the endpoint would 403 CHAT_STAFF_ONLY).
 */
export function useChatUnread(): number {
  const token = useAuthStore((s) => s.accessToken);
  const staff = useAuthStore((s) => isChatStaff(s.user));
  const { data } = useQuery({
    queryKey: chatKeys.unread,
    queryFn: chatApi.unreadCount,
    enabled: !!token && staff,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    staleTime: 10_000,
  });
  return data?.count ?? 0;
}
